import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Send } from "lucide-react";

export default function Support() {
    const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", subject: "", enquiry_type: "general", message: "" });
    const [loading, setLoading] = useState(false);
    const [ref, setRef] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/contact", form);
            setRef(data.reference);
            toast.success(`Enquiry received — ${data.reference}`);
            setForm({ name: "", email: "", organization: "", phone: "", subject: "", enquiry_type: "general", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong");
        }
        setLoading(false);
    };

    return (
        <main data-testid="support-page" className="pt-[72px]">
            <section className="bg-[#0B1930] text-white pt-24 pb-24 relative overflow-hidden grain">
                <div className="hero-glow top-[-30%] left-[-10%]" style={{ background: "#E8B923" }} />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
                    <div className="eyebrow">Support</div>
                    <h1 className="headline text-5xl md:text-6xl lg:text-7xl mt-6 max-w-3xl">
                        We're here to <em className="not-italic text-[#E8B923]">help</em>.
                    </h1>
                </div>
            </section>
            <section className="py-20 max-w-[1000px] mx-auto px-6 lg:px-10">
                {ref && (
                    <div className="bg-[#F7F9FC] border border-[#E8B923] rounded-xl p-6 mb-8" data-testid="support-success">
                        <div className="eyebrow">Enquiry Received</div>
                        <p className="text-navy mt-2">Your reference: <strong className="font-mono">{ref}</strong>. Our team will follow up within one business day.</p>
                    </div>
                )}
                <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                    <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="support-name" required />
                    <Field label="Email" value={form.email} type="email" onChange={(v) => setForm({ ...form, email: v })} testid="support-email" required />
                    <Field label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="support-org" />
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="support-phone" />
                    <div className="flex flex-col gap-2 md:col-span-2">
                        <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Enquiry Type</label>
                        <select data-testid="support-type" value={form.enquiry_type} onChange={(e) => setForm({ ...form, enquiry_type: e.target.value })} className="border border-slate-200 rounded-md px-4 py-3 bg-white">
                            <option value="general">General</option>
                            <option value="sales">Sales</option>
                            <option value="technical">Technical</option>
                            <option value="partnership">Partnership</option>
                        </select>
                    </div>
                    <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="support-subject" className="md:col-span-2" />
                    <div className="flex flex-col gap-2 md:col-span-2">
                        <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Message</label>
                        <textarea data-testid="support-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} className="border border-slate-200 rounded-md px-4 py-3 bg-white" />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button data-testid="support-submit" disabled={loading} className="btn-primary">
                            {loading ? "Sending…" : "Send Enquiry"} <Send className="w-4 h-4" />
                        </button>
                    </div>
                </form>
            </section>
        </main>
    );
}

function Field({ label, value, onChange, type = "text", required, testid, className = "" }) {
    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}{required && " *"}</label>
            <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="border border-slate-200 rounded-md px-4 py-3 bg-white" />
        </div>
    );
}
